// Components
import { CircularProgress, Typography } from '@mui/material'
import Alert from '@mui/material/Alert'
import LandingIcon from './LandingIcon/LandingIcon'

// Logic
import { useMainIconLandingLogic } from './MainIconLanding.logic'

// Styles
import classNames from 'classnames'
import './MainIconLanding.scss'

// Other
import { Helmet } from 'react-helmet'
import { CustomModal } from 'components/molecules/Modal/Modal'
import { MainIconLandingProps } from './MainIconLanding.types'
import { FC } from 'react'
import { SelectOldExplantsModal } from './SelectOldExplantsModal/SelectOldExplantsModal'
import { BatchDispositionModal } from 'components/BatchDispositionModal/BatchDispositionModal'
import { useNavigate } from 'react-router-dom'
import { AssignCardModal } from 'views/PreferenceCards/AssignCardModal/AssignCardModal'

const MainIconLanding: FC<MainIconLandingProps> = ({
  surgery,
  derivedSurgeryStatus,
  setOnMainIconLanding,
}) => {
  const {
    icons,
    loading,
    isMobile,
    oldImplants,
    twoColumnsClass,
    loadingOldImplants,
    selectedOldImplants,
    loadingBatchAddAssets,
    isAssignCardModalOpen,
    isDispositionModalOpen,
    selectOldExplantsModalOpen,
    showConsumablesAutopopulate,
    handleClick,
    populateConsumables,
    handleAutoExplantSave,
    setSelectedOldImplants,
    handleSaveUploadedMedia,
    setIsDispositionModalOpen,
    setSelectOldExplantsModalOpen,
    handleCloseAssignCardModal,
  } = useMainIconLandingLogic({ surgery, derivedSurgeryStatus })
  const navigate = useNavigate()

  const isPreApproval = derivedSurgeryStatus === 'PRE_APPROVAL'

  return (
    <div
      className={classNames('main-icon-landing_wrapper', {
        'is-mobile': isMobile,
      })}
    >
      {loadingOldImplants && (
        <div className="loader">
          <CircularProgress />
        </div>
      )}

      <Helmet>
        <title>Home</title>
      </Helmet>

      <Typography variant="h1" align="center">
        Select Product Type
      </Typography>

      {isPreApproval && (
        <Alert
          severity="warning"
          sx={{
            marginX: 2,
            '& a': {
              color: 'inherit',
              textDecoration: 'underline',
            },
            maxWidth: '800px',
          }}
        >
          This procedure is in a <strong>pre-approval</strong> state. Changes
          cannot be made until approval is completed or canceled. Please proceed
          to the{' '}
          <button
            className="pre-approval-link"
            onClick={() => {
              setOnMainIconLanding?.(false)
              navigate(`/surgeries/${surgery?._id}/list`)
            }}
          >
            Documented List
          </button>{' '}
          to complete the approval process.
        </Alert>
      )}

      <div className={`icons-container ${twoColumnsClass}`}>
        {Object.entries(icons).map(
          ([
            key,
            { IconComponent, label, dataTestId, isVisible, isDisabled },
          ]) => {
            if (isVisible === false) return null
            return (
              <LandingIcon
                key={key}
                isMobile={isMobile}
                onClick={() => handleClick(label)}
                isDisabled={isDisabled}
                icon={IconComponent}
                label={label}
                dataTestId={dataTestId}
              />
            )
          }
        )}
      </div>

      {showConsumablesAutopopulate && (
        <CustomModal
          header="Consumables Autopopulate"
          fitContentWidth={!isMobile}
          open={showConsumablesAutopopulate}
          isLoadingState={loading}
          primaryButtonText="YES"
          secondaryButtonText="NO"
          onCloseTakeSecondaryButtonAction={false}
          secondaryButtonAction={() => populateConsumables(false)}
          handleOnSubmit={() => populateConsumables(true)}
          icon={<></>}
        >
          <Typography>
            A preference card was found for this surgery, Would you like to add
            all the consumables from it?
          </Typography>
        </CustomModal>
      )}

      {selectOldExplantsModalOpen && (
        <SelectOldExplantsModal
          isLoading={loadingOldImplants}
          oldImplants={oldImplants}
          open={selectOldExplantsModalOpen}
          onClose={() => setSelectOldExplantsModalOpen(false)}
          onSetIsDispositionModalOpen={setIsDispositionModalOpen}
          onSetSelectedOldImplants={setSelectedOldImplants}
        />
      )}

      {isDispositionModalOpen && (
        <BatchDispositionModal
          isAutoExplant
          isOpen={isDispositionModalOpen}
          scan={selectedOldImplants[0]}
          selectedScans={selectedOldImplants}
          assetType="explanted"
          dispositionStatus="EXPLANTED"
          isManualAddition={false}
          activeTray={surgery?.activeAssetTray || undefined}
          assetTrays={surgery?.assetTrays || []}
          onAutoExplantSave={handleAutoExplantSave}
          onSaveMedia={handleSaveUploadedMedia}
          onClose={() => {
            setIsDispositionModalOpen(false)
          }}
          isSubmitting={loadingBatchAddAssets}
          setIsTrayScannerOpen={() => {}}
        />
      )}

      {isAssignCardModalOpen && (
        <AssignCardModal
          open={isAssignCardModalOpen}
          onClose={handleCloseAssignCardModal}
          onCreateNew={() => {
            handleCloseAssignCardModal()
            navigate(`/preference-cards/new`, {
              state: {
                surgeonId: surgery.attendingSurgeon?.provider._id,
                procedureType: surgery.procedures[0]?.description,
              },
            })
          }}
          surgery={surgery}
        />
      )}
    </div>
  )
}

export default MainIconLanding
